// lockerPanel.ts — P2.5 the SKIN LOCKER (grew out of the P2.3 "Muncher Skins" store): free
// mastery-earned muncher skins on the Tactical OLED redline. A full-bleed modal with a compact
// header band (live tinted hero preview + "N/M unlocked" + the next-goal nudge + the dark-store
// teaser chip) over a list of skin cards — equipped / unlocked / locked-with-EXACT-progress
// ("4/10 wins" + goal sentence + a slim progress bar). Equip on tap (persisted via cosmetics.ts).
// The row step is derived from the card height so all six cards + the DONE footer fit the 360×640
// gate viewport (the P2.3 fixed 76px step overflowed the footer there). FREE unlocks only; the
// "Infinite Topics" chip keeps the deliberately-dark store surface reachable (cosmeticIapLive()
// ships false — no purchase/network path). Pure UI over cosmetics.ts + the shared modal/kit.

import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { modal } from './ui';
import { scaledFont, motionAllowed } from './settings';
import { audio } from './audio';
import { icon } from './icons';
import { openStore } from './storePanel';
import { listRow, ctaButton, chip, labelText, progressBar } from './ui/kit';
import { muncher, applyCosmeticSkin } from './muncher';
import {
  COSMETICS,
  cosmeticIapLive,
  cosmeticProgress,
  isCosmeticUnlocked,
  nextCosmeticGoal,
  selectCosmetic,
  selectedCosmetic,
  unlockedCosmeticCount,
} from './cosmetics';

export function openLocker(scene: Phaser.Scene): ReturnType<typeof modal> {
  // S3 item 11/15: content-hugging — the header band (100) + six skin rows + DONE sized to fit, so the
  // panel no longer keeps a large dead band below the last card (the row step derives from the hugged
  // height, so all six stay visible above the footer). Clamped to the viewport by modal().
  const desktop = scene.scale.width >= 900;
  const m = modal(scene, {
    title: 'SKIN LOCKER',
    contentHeight: desktop ? 560 : 548,
    railGrad: [PALETTE.player, PALETTE.rival],
    widthPx: desktop ? 940 : undefined,
  });
  const { card, cardW, bodyTop, footerY } = m;
  let selId = selectedCosmetic().id;

  // — layout anchors (card-local). Row step adapts to the card height so 6 cards always fit. —
  const rowW = cardW - 48;
  const bandTop = bodyTop + 6;
  const heroX = -rowW / 2 + 44;
  const heroY = bandTop + 46;
  const textX = -rowW / 2 + 96;
  const listTop = bandTop + 100;
  const avail = footerY - 34 - listTop;
  const rowStep = Math.max(56, Math.min(76, Math.floor(avail / COSMETICS.length)));
  const rowH = Math.min(64, rowStep - 8);

  // — header band: unlock count + next-goal nudge + the dark-store teaser chip —
  card.add(labelText(scene, textX, bandTop + 18, `${unlockedCosmeticCount()} / ${COSMETICS.length} unlocked`));
  card.add(
    chip(scene, rowW / 2, bandTop + 18, cosmeticIapLive() ? 'Store' : 'Store Off', {
      icon: cosmeticIapLive() ? 'infinity' : 'lock',
      textColor: PALETTE.gold,
      borderColor: PALETTE.gold,
      originX: 1,
      fontPx: 11,
      onClick: () => openStore(scene),
    }),
  );
  const goal = nextCosmeticGoal();
  const goalFs = `${scaledFont(13, 11)}px`;
  if (goal) {
    // "Next: <Skin> — 6 wins to go" with the skin name highlighted; scaled down if it overflows.
    const pre = scene.add.text(0, 0, 'Next: ', { fontFamily: FONT.family, fontSize: goalFs, color: hex(PALETTE.dim) }).setOrigin(0, 0.5);
    const name = scene.add
      .text(0, 0, goal.cosmetic.label, { fontFamily: FONT.family, fontSize: goalFs, color: hex(PALETTE.player), fontStyle: '600' })
      .setOrigin(0, 0.5);
    const rest = scene.add
      .text(0, 0, ` — ${goal.progress.remaining}`, { fontFamily: FONT.family, fontSize: goalFs, color: hex(PALETTE.dim) })
      .setOrigin(0, 0.5);
    const group = scene.add.container(textX, bandTop + 48, [pre, name, rest]);
    name.setX(pre.width);
    rest.setX(pre.width + name.width);
    const groupW = pre.width + name.width + rest.width;
    const maxW = rowW - 100;
    if (groupW > maxW) group.setScale(maxW / groupW);
    card.add(group);
  } else {
    card.add(
      scene.add
        .text(textX, bandTop + 48, `All ${COSMETICS.length} skins unlocked — legend!`, {
          fontFamily: FONT.family,
          fontSize: goalFs,
          color: hex(PALETTE.dim),
        })
        .setOrigin(0, 0.5),
    );
  }

  // — dynamic layer (hero + cards), rebuilt on equip so preview + states recolour together —
  const dyn = scene.add.container(0, 0);
  card.add(dyn);
  let heroObj: Phaser.GameObjects.Sprite | Phaser.GameObjects.Graphics | null = null;

  const render = (): void => {
    dyn.removeAll(true);
    heroObj = null;

    // Hero: a soft glow disc + YOUR muncher (the in-game animated sheet when it decoded, so the
    // preview is exactly what the board shows) in the equipped tint.
    const sel = COSMETICS.find((c) => c.id === selId) ?? COSMETICS[0];
    const glowTint = sel.tint ?? PALETTE.player;
    const disc = scene.add.graphics({ x: heroX, y: heroY });
    disc.fillStyle(glowTint, 0.07);
    disc.fillCircle(0, 0, 42);
    disc.fillStyle(glowTint, 0.13);
    disc.fillCircle(0, 0, 27);
    dyn.add(disc);
    if (scene.textures.exists('msPlayerIdle')) {
      const spr = scene.add.sprite(heroX, heroY, 'msPlayerIdle');
      // S3 item 15: a larger equipped-skin preview (78 vs 64) reads as the hero of the locker.
      spr.setScale((78 * 1.15) / Math.max(spr.width || 1, spr.height || 1));
      applyCosmeticSkin(spr, sel);
      if (motionAllowed() && scene.anims.exists('mun-player-idle')) spr.play('mun-player-idle');
      else spr.setFrame(0);
      dyn.add(spr);
      heroObj = spr;
    } else {
      const g = muncher(scene, 'player', heroX, heroY, 78, { tint: sel.tint ?? undefined });
      dyn.add(g);
      heroObj = g;
    }

    // Skin cards. Every card is NAMED (colorblind-safe: never hue alone) and a locked card shows
    // its goal sentence + exact progress numbers + a slim progress bar.
    COSMETICS.forEach((c, i) => {
      const y = listTop + rowH / 2 + i * rowStep;
      const unlocked = isCosmeticUnlocked(c);
      const equipped = c.id === selId;
      const nextTarget = !unlocked && goal?.cosmetic.id === c.id;
      const prog = cosmeticProgress(c);
      const valueStr = unlocked ? (equipped ? 'EQUIPPED' : 'EQUIP') : (prog?.progress ?? 'LOCKED');
      // S3 item 13: locked gate values ("0/3 facts") use the vetted lockGlyph grey (clears WCAG-AA
      // 4.5:1 on every board bg) instead of the lighter dim, so they stay legible under the row dim.
      const valueColor = unlocked ? (equipped ? PALETTE.correct : PALETTE.scan) : PALETTE.lockGlyph;
      const row = listRow(scene, 0, y, rowW, {
        swatch: c.tint ?? PALETTE.player,
        label: c.label,
        labelColor: unlocked || nextTarget ? PALETTE.text : PALETTE.dim,
        sublabel: !unlocked && prog ? prog.goal : undefined,
        value: valueStr,
        valueColor: nextTarget ? PALETTE.gold : valueColor,
        height: rowH,
        fill: equipped || nextTarget ? PALETTE.surface : PALETTE.tile,
        border: equipped ? PALETTE.correct : nextTarget ? PALETTE.gold : PALETTE.line,
      });
      if (!unlocked) {
        // A geometric lock glyph sits just left of the progress numbers; dim the whole card.
        const measure = scene.add.text(0, 0, valueStr, { fontFamily: FONT.display, fontSize: `${scaledFont(15, 12)}px`, fontStyle: 'bold' });
        const vw = measure.width;
        measure.destroy();
        row.add(icon(scene, 'lock', rowW / 2 - 16 - vw - 10, 0, 13, PALETTE.dim));
        // Lighter row dim (0.85 vs 0.75) so the gate value + goal keep ≥4.5:1 contrast (S3 item 13).
        row.setAlpha(0.95);
      }
      dyn.add(row);
      if (!unlocked && prog) {
        // Slim progress bar under the goal sentence (aligned with the label column).
        const bar = progressBar(scene, -rowW / 2 + 48, y + rowH / 2 - 7, Math.min(rowW * 0.4, 170), 3, prog.frac, PALETTE.scan);
        bar.container.setAlpha(0.9);
        dyn.add(bar.container);
      }
      const hit = scene.add.rectangle(0, y, rowW, rowH, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => {
        if (!isCosmeticUnlocked(c)) {
          audio().playSfx('lock', { volume: 0.5 });
          return;
        }
        if (selectCosmetic(c.id)) {
          selId = c.id;
          audio().playSfx('uiConfirm');
          render();
          if (motionAllowed() && heroObj) {
            const s0 = heroObj.scale || 1;
            scene.tweens.add({ targets: heroObj, scale: { from: s0 * 0.86, to: s0 }, duration: 240, ease: 'Back.easeOut' });
          }
        }
      });
      dyn.add(hit);
    });
  };
  render();

  // — DONE —
  card.add(ctaButton(scene, 0, footerY, 'Done', () => m.close(), { width: cardW - 48, height: 56 }));

  return m;
}
